// The GCS resumable-upload protocol, as the browser speaks it (ENG-1820).
//
// Deliberately dependency-free — no `@/lib/api`, no auth, no app types. Two
// reasons: the transfer talks to storage.googleapis.com with a *signed* URL and
// must not carry our Authorization headers, and keeping it pure makes the
// resume arithmetic (the part with real edge cases) unit-testable without
// standing up the app's module graph. `api/uploads-direct.ts` is the thin layer
// that wires this to our endpoints.
//
// The protocol, in three moves:
//   1. POST a signed URL with `x-goog-resumable: start` → session URI in the
//      `Location` response header.
//   2. PUT the bytes to the session URI.
//   3. If that dies mid-transfer, PUT an empty body with
//      `Content-Range: bytes *​/<total>` to ask how far GCS got, then send only
//      the remainder. This is the whole reason for using the resumable protocol
//      rather than a single signed PUT: a 150 MB upload that fails at 90% must
//      not start over.

/** A failure the caller should surface rather than resume.
 *
 * `terminal` distinguishes "this will never work" (expired session, bad range,
 * refused by GCS) from "the connection dropped" — only the latter is worth
 * retrying, and retrying the former would just burn the retry budget before
 * showing the user the same error. */
export class ResumableUploadError extends Error {
  readonly terminal: boolean;
  readonly status?: number;

  constructor(message: string, opts: { terminal?: boolean; status?: number } = {}) {
    super(message);
    this.name = "ResumableUploadError";
    this.terminal = opts.terminal ?? false;
    this.status = opts.status;
  }
}

/** How many times a stalled transfer is resumed before giving up. Each retry
 * re-queries the committed offset first, so retries make forward progress
 * rather than repeating work — a generous budget is cheap, and a flaky uplink
 * on a multi-minute upload is common. */
export const MAX_RESUME_ATTEMPTS = 5;
const RESUME_BASE_DELAY_MS = 1_000;

/** Percentage to display for `loaded` of `total`.
 *
 * Reaches 100 exactly when the body is fully sent — NOT when the Source exists.
 * That boundary is what the Sources modal keys "Finishing…" and, critically,
 * *hiding Cancel* off: once every byte is in GCS, cancelling is ambiguous
 * rather than clean (the server may already be finalizing, and the abort can't
 * stop the worker thread), so the affordance has to disappear at exactly this
 * point. Same contract as the multipart path, which reports 100 at body-sent
 * for the same reason. Rounding is clamped below 100 so it can't arrive early
 * at ~99.5% and hide Cancel while bytes are still moving. */
export function progressPct(loaded: number, total: number): number {
  if (!total) return 0;
  return loaded >= total ? 100 : Math.min(99, Math.floor((loaded / total) * 100));
}

/** Byte offset to resume from, given a 308's `Range` header.
 *
 * GCS reports the *inclusive last byte it has* (`bytes=0-99` means 100 bytes
 * committed), so the next offset is one past it. The header is absent when
 * nothing landed — which is not an error, just an offset of 0. */
export function parseCommittedOffset(rangeHeader: string | null | undefined): number {
  if (!rangeHeader) return 0;
  const last = Number(rangeHeader.split("-")[1]);
  return Number.isFinite(last) && last >= 0 ? last + 1 : 0;
}

export type UploadProgress = { loaded: number; total: number; pct: number };

/** Sends `[offset, total)` and resolves once GCS reports the object complete. */
export type RangeSender = (args: {
  sessionUri: string;
  file: Blob;
  offset: number;
  total: number;
  onProgress?: (p: UploadProgress) => void;
  signal?: AbortSignal;
}) => Promise<void>;

/** Reports the committed byte count, or `"complete"` if the object already landed. */
export type OffsetQuery = (
  sessionUri: string,
  total: number,
  signal?: AbortSignal,
) => Promise<number | "complete">;

/** 4xx statuses that are transient despite the class — a request timeout and a
 * throttle. GCS's own retry policy treats both as retryable. */
const RESUMABLE_4XX = new Set([408, 429]);

/** Exchange a signed start URL for a GCS resumable session URI. */
export async function openResumableSession(
  startUrl: string,
  headers: Record<string, string>,
  signal?: AbortSignal,
): Promise<string> {
  const resp = await fetch(startUrl, {
    // Exactly the headers the signature committed to — GCS recomputes the
    // signature over them, so any deviation is rejected by GCS itself.
    method: "POST",
    headers,
    body: "",
    signal,
  });
  if (!resp.ok) {
    throw new ResumableUploadError(
      `Couldn't start the upload (HTTP ${resp.status}). Please try again.`,
      { terminal: resp.status >= 400 && resp.status < 500, status: resp.status },
    );
  }
  const location = resp.headers.get("location");
  if (!location) {
    // Almost always the bucket's CORS `responseHeader` list missing `location`.
    // Name the cause: the fix is a bucket config change, not a retry.
    throw new ResumableUploadError(
      "Couldn't start the upload — the storage bucket didn't return a session URL.",
      { terminal: true },
    );
  }
  return location;
}

/** Force the progress reading to 100 on a SUCCESSFUL transfer exit.
 *
 * XHR does not guarantee a final `progress` event, and the lost-response
 * recovery path never sends one at all — so both success returns could leave
 * the bar short of 100 with every byte committed. That is not cosmetic: the
 * Sources modal shows Cancel while `pct < 100`, and by this point `/complete`
 * is about to start (or has). Cancelling then removes the only row still
 * holding the upload token without stopping the server worker, and re-picking
 * the file duplicates the Source. Reaching 100 is what retires the button. */
function reportComplete(onProgress: ((p: UploadProgress) => void) | undefined, total: number): void {
  onProgress?.({ loaded: total, total, pct: 100 });
}

/**
 * PUT `file` to a resumable session, resuming from the committed offset when
 * the connection drops.
 *
 * `send` and `queryOffset` are injectable so the resume arithmetic can be
 * tested without a real XHR; production always uses the defaults below.
 */
export async function putResumable(
  sessionUri: string,
  file: Blob,
  opts: {
    onProgress?: (p: UploadProgress) => void;
    signal?: AbortSignal;
    send?: RangeSender;
    queryOffset?: OffsetQuery;
    sleep?: (ms: number, signal?: AbortSignal) => Promise<void>;
  } = {},
): Promise<void> {
  const send = opts.send ?? sendRangeViaXhr;
  const queryOffset = opts.queryOffset ?? queryCommittedOffset;
  const sleep = opts.sleep ?? delay;
  const total = file.size;
  let offset = 0;

  for (let attempt = 0; attempt <= MAX_RESUME_ATTEMPTS; attempt++) {
    try {
      await send({
        sessionUri,
        file,
        offset,
        total,
        onProgress: opts.onProgress,
        signal: opts.signal,
      });
      reportComplete(opts.onProgress, total);
      return;
    } catch (e) {
      // A user cancel and a terminal rejection are both final — only a
      // transport failure is worth resuming.
      if (isAbort(e)) throw e;
      if (e instanceof ResumableUploadError && e.terminal) throw e;
      if (attempt === MAX_RESUME_ATTEMPTS) throw e;
      await sleep(RESUME_BASE_DELAY_MS * 2 ** attempt, opts.signal);
      // Ask again until GCS answers, rather than sending from an offset we know
      // is stale. The failed send may well have committed more bytes, so the
      // last verified offset is no longer the one the session expects — and a
      // `Content-Range` that disagrees with the session is rejected as a
      // terminal 4xx, costing the whole transfer. Re-probing is cheap; sending
      // from a stale offset risks everything.
      let committed: number | "complete" | undefined;
      for (let probe = 0; probe <= MAX_RESUME_ATTEMPTS && committed === undefined; probe++) {
        try {
          committed = await queryOffset(sessionUri, total, opts.signal);
        } catch (probeError) {
          if (isAbort(probeError)) throw probeError;
          // The session is gone — no offset will ever come back, so stop rather
          // than spending the rest of the budget asking.
          if (probeError instanceof ResumableUploadError && probeError.terminal) throw probeError;
          if (probe === MAX_RESUME_ATTEMPTS) throw e;
          await sleep(RESUME_BASE_DELAY_MS * 2 ** probe, opts.signal);
        }
      }
      if (committed === undefined) throw e;
      // The object is already there — the *response* was what got lost, not the
      // upload. Treat it as success rather than re-sending the whole file.
      if (committed === "complete") {
        reportComplete(opts.onProgress, total);
        return;
      }
      // A retry with NO forward progress is still worth making. On a flaky
      // connection a resumed send can drop before GCS commits another byte, and
      // the next one succeeds — bailing on the first flat offset spent only two
      // of the five attempts the budget allows, and re-sending from the verified
      // offset repeats nothing already committed. `MAX_RESUME_ATTEMPTS` plus the
      // exponential backoff is the bound; a second, tighter heuristic here just
      // made it a weaker one.
      offset = committed;
      opts.onProgress?.({ loaded: offset, total, pct: progressPct(offset, total) });
    }
  }
}

/**
 * Ask GCS how many bytes of the session it has committed.
 *
 * The status-probe form of the protocol: a PUT with an empty body and
 * `Content-Range: bytes *​/<total>`. 308 carries `Range: bytes=0-<last>`;
 * 200/201 means the object is already complete.
 */
export async function queryCommittedOffset(
  sessionUri: string,
  total: number,
  signal?: AbortSignal,
): Promise<number | "complete"> {
  try {
    const resp = await fetch(sessionUri, {
      method: "PUT",
      headers: { "Content-Range": `bytes */${total}` },
      signal,
    });
    if (resp.status === 200 || resp.status === 201) return "complete";
    // ONLY a 308 carries the resumable `Range`. Reading an offset out of
    // anything else means reading it out of a response that never had one —
    // which parses as 0 and silently replaces a verified offset, re-sending
    // bytes GCS already holds. Everything else is classified, not parsed.
    if (resp.status === 308) return parseCommittedOffset(resp.headers.get("range"));
    if (RESUMABLE_4XX.has(resp.status) || resp.status >= 500) {
      throw new ResumableUploadError(`couldn't read the committed offset (HTTP ${resp.status})`, {
        status: resp.status,
      });
    }
    // An expired or unknown session (404/410) or a refusal: resuming can't fix
    // it, and the caller should stop rather than spend its budget.
    throw new ResumableUploadError("The upload session expired. Please try again.", {
      terminal: true,
      status: resp.status,
    });
  } catch (e) {
    if (isAbort(e)) throw e;
    // A classified failure from above passes straight through — in particular a
    // terminal one, which the caller must not retry.
    if (e instanceof ResumableUploadError) throw e;
    // Transport failure. Do NOT guess: answering "0" for a session that has a
    // committed prefix sends the caller back to the start on a session that
    // expects otherwise, and the resulting mismatch is terminal — a flaky probe
    // would cost the whole transfer. The caller retries from the last offset it
    // actually verified instead.
    throw new ResumableUploadError("couldn't read the committed offset");
  }
}

/** The production transport. XHR rather than `fetch` because `fetch` cannot
 * report request-body progress, and a multi-minute upload with no progress bar
 * is indistinguishable from a hang. */
export const sendRangeViaXhr: RangeSender = ({
  sessionUri,
  file,
  offset,
  total,
  onProgress,
  signal,
}) =>
  new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const onAbort = () => xhr.abort();
    const cleanup = () => signal?.removeEventListener("abort", onAbort);
    if (signal?.aborted) {
      reject(new DOMException("Upload canceled", "AbortError"));
      return;
    }
    xhr.open("PUT", sessionUri, true);
    if (total > 0) {
      // ALWAYS declare the range, including a full send from 0. A resumable
      // session that already holds a committed prefix rejects a body with no
      // Content-Range — so the "safe" full re-send was the one thing GCS
      // wouldn't take, turning a recoverable stall into a terminal restart.
      xhr.setRequestHeader("Content-Range", `bytes ${offset}-${total - 1}/${total}`);
    }
    xhr.upload.onprogress = (e) => {
      const loaded = Math.min(offset + e.loaded, total);
      onProgress?.({ loaded, total, pct: progressPct(loaded, total) });
    };
    xhr.onload = () => {
      cleanup();
      if (xhr.status === 200 || xhr.status === 201) {
        resolve();
      } else if (xhr.status === 308) {
        // GCS wants more. We never deliberately send a partial range, so treat
        // this as a transport-level retry and let the resume loop re-read the
        // offset rather than guessing.
        reject(new ResumableUploadError("upload incomplete", { status: 308 }));
      } else if (RESUMABLE_4XX.has(xhr.status)) {
        // 408/429 are 4xx by status but transient by meaning — a request
        // timeout and a throttle. Calling them terminal made a slow or busy
        // moment cost the user the entire transfer, when the committed prefix
        // was sitting right there to resume from.
        reject(new ResumableUploadError(`upload failed (HTTP ${xhr.status})`, {
          status: xhr.status,
        }));
      } else if (xhr.status >= 400 && xhr.status < 500) {
        // Expired session, bad range, refused — resuming can't fix any of it.
        reject(
          new ResumableUploadError("The upload session expired. Please try again.", {
            terminal: true,
            status: xhr.status,
          }),
        );
      } else {
        reject(new ResumableUploadError(`upload failed (HTTP ${xhr.status})`, {
          status: xhr.status,
        }));
      }
    };
    xhr.onerror = () => {
      cleanup();
      reject(new ResumableUploadError("upload connection failed"));
    };
    xhr.onabort = () => {
      cleanup();
      reject(new DOMException("Upload canceled", "AbortError"));
    };
    signal?.addEventListener("abort", onAbort, { once: true });
    xhr.send(offset > 0 ? file.slice(offset) : file);
  });

/** Did this 404 mean "that route doesn't exist" rather than "that thing
 * doesn't exist"?
 *
 * The distinction decides whether a large upload is told "this deployment
 * can't take a file this size" or the truth about what it asked for. Netlify
 * publishes the web app from `main` on every push while the API deploys
 * separately and later, so there is a real window where this client calls an
 * endpoint the live API has never heard of — degrading gracefully there is the
 * point. But the same endpoint deliberately answers 404 for an unknown,
 * deleted, or cross-tenant project (404 rather than 403, so the API never
 * confirms what the caller can't see), and mislabelling THAT as a size problem
 * is a false and permanent dead end.
 *
 * They're told apart by the body: a routing miss is the framework's own
 * generic `{"detail": "Not Found"}`, while every 404 this API raises on purpose
 * carries a specific detail ("Project not found", "upload not found"). */
export function isMissingRoute(status: number, detail?: string | null): boolean {
  return status === 404 && (detail ?? "").trim().toLowerCase() === "not found";
}

/** Should a failed `/complete` be retried on the SAME upload token?
 *
 * `status` is the HTTP status the finalize came back with, or `null` when the
 * request never got a response at all (network drop, DNS, connection reset).
 *
 * Retrying is only possible because `/complete` is idempotent on the token: a
 * second attempt replays the first one's Source rather than creating another.
 * It also *matters*, because the alternative is worse than a spinner. Callers
 * that don't retain the resume state — the composer removes a failed
 * attachment and the user picks the file again — mint a NEW token on the next
 * try, while the original worker may still be finalizing. That produces the
 * duplicate Source this whole path exists to prevent. So an ambiguous failure
 * is retried here, where the token is still in hand.
 *
 * `null` is the most important case: with no response, whether the server
 * created the Source is simply unknowable from the client.
 *
 * 409 means another worker holds the finalize lease. Retrying the same token
 * either replays its Source once it lands or 409s again — never duplicates.
 *
 * A terminal 4xx (400 rejected bytes, 404 unknown token, 403) will fail
 * identically forever, so retrying only delays the error the user needs. */
export function shouldRetryFinalize(status: number | null): boolean {
  if (status === null) return true;
  if (status === 408 || status === 409 || status === 429) return true;
  return status >= 500;
}

/** Whether a 401 from finalize is worth one more attempt.
 *
 * The finalize client opts out of `apiFetch`'s built-in refresh-and-resend,
 * because that resend carries the SAME body — including a `poll_only` verdict
 * the refresh round trip may have invalidated. It has to keep the recovery that
 * retry provided, though: without it a merely expired token kills an upload
 * that was about to succeed. So the retry moves up into the attempt loop, where
 * the next body is rebuilt from a current clock.
 *
 * Once. A 401 that survives a forced refresh is a real authentication failure,
 * and grinding on it for the rest of the deadline only delays telling the user. */
export function finalizeAuthRetryAvailable(status: number | null, alreadyRetried: boolean): boolean {
  return status === 401 && !alreadyRetried;
}

/** Whether the next finalize attempt may let the server START work.
 *
 * A finalize worker has a bounded lifetime, and so does this client's wait.
 * Late enough in that wait there is no longer room for a whole worker: one
 * started now would still be running after the loop gave up and the handle was
 * dropped, and could create a Source nobody is waiting for — which the user's
 * re-pick then duplicates.
 *
 * Note what this deliberately does NOT do: infer from the previous response.
 * A 409 proves a worker held the lease when that reply was *produced*, not that
 * the next request will find one still there. Reading it as "so the retry is a
 * free poll" is wrong precisely when it matters — the worker died in between,
 * and the retry takes the claim over and starts a fresh one. The client asks
 * for a poll-only completion instead of guessing. */
export function mayStartFinalizeWork(remainingMs: number, workerWorstCaseMs: number): boolean {
  return remainingMs >= workerWorstCaseMs;
}

/** What to do with a failed attempt inside `attemptUntilDeadline`. */
export type RetryVerdict =
  /** Our own per-attempt timeout fired. The work is very likely still running
   * server-side, so go again at once rather than spending backoff on it. */
  | "immediate"
  /** Ambiguous but worth replaying — wait, then retry. */
  | "backoff"
  /** Terminal. Surface it. */
  | "fatal";

/** Retry `attempt` until it succeeds, turns fatal, or the deadline passes.
 *
 * ONE deadline rather than a budget per failure kind, because the kinds convert
 * into each other: an attempt that outruns its own timeout leaves the server
 * still working, so the next attempt on the same handle fails fast — and with
 * separate budgets those fast failures drain the fast allowance and give up
 * while most of the deadline is still unspent, which is exactly the case the
 * long wait existed for. Pinning the total is the only bound that can't be
 * starved by the shape of the failures.
 *
 * `classify` receives the time still left on the deadline, because not every
 * retry costs the same. Some are free polls against work already running; others
 * make the server START something new, and starting work that cannot finish
 * before this deadline is worse than giving up — the caller abandons its handle
 * while the server is still able to act on it.
 *
 * `now`/`sleep` are injectable so this is testable without real time. */
export async function attemptUntilDeadline<T>(
  attempt: (timeoutMs: number, remainingMs: number, remainingNow: () => number) => Promise<T>,
  opts: {
    deadlineMs: number;
    attemptTimeoutMs: number;
    backoffMs: readonly number[];
    classify: (e: unknown, remainingMs: number) => RetryVerdict;
    onDeadlineExceeded: () => Error;
    now?: () => number;
    sleep?: (ms: number) => Promise<void>;
  },
): Promise<T> {
  const now = opts.now ?? (() => Date.now());
  const sleep = opts.sleep ?? ((ms: number) => new Promise((r) => setTimeout(r, ms)));
  const deadline = now() + opts.deadlineMs;
  let backoffs = 0;
  for (;;) {
    const remaining = deadline - now();
    if (remaining <= 0) throw opts.onDeadlineExceeded();
    try {
      // `remaining` is a snapshot taken before the attempt starts. Anything the
      // attempt does BEFORE committing to work — acquiring an auth token, a
      // forced refresh — spends budget the snapshot can't see, so a decision
      // that depends on how much time is left must re-read the clock rather
      // than trust the snapshot it was handed.
      return await attempt(
        Math.min(opts.attemptTimeoutMs, remaining),
        remaining,
        () => deadline - now(),
      );
    } catch (e) {
      const verdict = opts.classify(e, deadline - now());
      if (verdict === "fatal") throw e;
      if (verdict === "backoff") {
        const wait = opts.backoffMs[Math.min(backoffs++, opts.backoffMs.length - 1)];
        const left = deadline - now();
        if (left <= 0) throw opts.onDeadlineExceeded();
        await sleep(Math.min(wait, left));
      }
    }
  }
}

export function isAbort(e: unknown): boolean {
  return e instanceof DOMException && e.name === "AbortError";
}

function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException("Upload canceled", "AbortError"));
    };
    if (signal?.aborted) {
      reject(new DOMException("Upload canceled", "AbortError"));
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}
